import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http from "../api/http";
import User from "../types/user";
import Option from "../types/option";
import { getCompany } from "./company";

interface UserState {
  status: "idle" | "loading" | "failed";
  user?: User;
  userList: User[];
  userRoles?: Option[];
}

const initialState: UserState = {
  status: "idle",
  userList: [],
};

export const fetchUserList = createAsyncThunk<User[]>(
  "users/fetch-all",
  async () => {
    const response = await http.get<User[]>("/user", {
      params: {
        refreshToken: true,
      },
    });
    return response.data;
  }
);

export const createUser = createAsyncThunk<User, User>(
  "users/add",
  async (vm: User) => {
    const response = await http.post<User>(`/user`, vm, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const editUser = createAsyncThunk<User, User>(
  "users/edit",
  async (vm: User) => {
    const response = await http.put<User>(`/user/${vm.id}`, vm, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const deleteUser = createAsyncThunk<null, number>(
  "users/delete",
  async (id: number) => {
    const response = await http.delete(`/user/${id}`, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const getRoles = createAsyncThunk<Option[]>(
  "users/fetch-roles",
  async () => {
    const response = await http.get<Option[]>(`/user/roles`, {
      params: {
        refreshToken: true,
      },
    });
    return response.data;
  }
);

export const getUser = createAsyncThunk<User, number>(
  "users/fetch",
  async (id: number) => {
    const response = await http.get<User>(`/user/${id}`, {
      params: {
        refreshToken: true,
      },
    });
    return response.data;
  }
);

export const getProfile = createAsyncThunk<User>(
  "users/fetch-profile",
  async () => {
    const response = await http.get<User>(`/user/profile`, {
      params: {
        refreshToken: true,
      },
    });
    return response.data;
  }
);

export const resendInvitation = createAsyncThunk(
  "auth/resend",
  async (id: number) => {
    const response = await http.patch(`/user/${id}`, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    clearUser(state) {
      state.user = initialState.user;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserList.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchUserList.fulfilled, (state, action) => {
        state.status = "idle";
        state.userList = action.payload;
      })
      .addCase(fetchUserList.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(createUser.pending, (state) => {
        state.status = "loading";
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.status = "idle";
        state.user = action.payload;
      })
      .addCase(createUser.rejected, (state) => {
        state.status = "failed";
      })

      .addCase(editUser.pending, (state) => {
        state.status = "loading";
      })
      .addCase(editUser.fulfilled, (state, action) => {
        state.status = "idle";
        state.user = action.payload;
      })
      .addCase(editUser.rejected, (state) => {
        state.status = "failed";
      })

      .addCase(deleteUser.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteUser.fulfilled, (state) => {
        state.status = "idle";
        state.user = undefined;
      })
      .addCase(deleteUser.rejected, (state) => {
        state.status = "failed";
      })

      .addCase(getUser.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getUser.fulfilled, (state, action) => {
        state.status = "idle";
        state.user = action.payload;
      })
      .addCase(getUser.rejected, (state) => {
        state.status = "failed";
      })

      .addCase(getRoles.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getRoles.fulfilled, (state, action) => {
        state.status = "idle";
        state.userRoles = action.payload;
      })
      .addCase(getRoles.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getCompany.fulfilled, (state, action) => {
        if (!action.payload) {
          state.user = initialState.user;
          state.userList = initialState.userList;
          state.userRoles = initialState.userRoles;
        }
      });
  },
});

export const { clearUser } = userSlice.actions;

export default userSlice.reducer;
